// ============================================
// OFFICEFORGED — Donut Selector
// Direct port of ProfitForged donut to vanilla JS
// Navy blue coin instead of red. Same dimensions.
// Container: 480x480, ring: 380/320, items at r=168
// ============================================

const ITEMS = [
  // HOME (0) — 12:00
  { id: 'home',      icon: '🏠', label: 'Home' },
  // PRODUCTIVITY (1-3) — 1:00 → 4:00
  { id: 'docs',      icon: '📄', label: 'Docs' },
  { id: 'grid',      icon: '⊞',  label: 'Grid' },
  { id: 'pdf',       icon: '📋', label: 'PDF' },
  // COMMUNICATION (4-6) — 5:00 → 7:00
  { id: 'chat',      icon: '💬', label: 'Chat' },
  { id: 'video',     icon: '🎥', label: 'Video' },
  { id: 'phone',     icon: '📞', label: 'Phone' },
  // MANAGEMENT (7-9) — 8:00 → 11:00
  { id: 'files',     icon: '📁', label: 'Files' },
  { id: 'dashboard', icon: '📊', label: 'Dash' },
  { id: 'settings',  icon: '⚙',  label: 'Settings' },
];

const CATEGORIES = [
  { id: 'home',  label: 'HOME',           startIndex: 0, endIndex: 0, color: '#059669' },
  { id: 'prod',  label: 'PRODUCTIVITY',   startIndex: 1, endIndex: 3, color: '#3B82F6' },
  { id: 'comm',  label: 'COMMUNICATION',  startIndex: 4, endIndex: 6, color: '#0D9488' },
  { id: 'mgmt',  label: 'MANAGEMENT',     startIndex: 7, endIndex: 9, color: '#7C3AED' },
];

function getCatForIndex(i) {
  return CATEGORIES.find(c => i >= c.startIndex && i <= c.endIndex) || null;
}

function makeWedgePath(cx, cy, ir, or, startDeg, endDeg) {
  const toRad = d => d * Math.PI / 180;
  const s = toRad(startDeg), e = toRad(endDeg);
  const isx = cx + ir * Math.cos(s), isy = cy + ir * Math.sin(s);
  const iex = cx + ir * Math.cos(e), iey = cy + ir * Math.sin(e);
  const osx = cx + or * Math.cos(s), osy = cy + or * Math.sin(s);
  const oex = cx + or * Math.cos(e), oey = cy + or * Math.sin(e);
  const la = (endDeg - startDeg) > 180 ? 1 : 0;
  return `M${isx},${isy} L${osx},${osy} A${or},${or} 0 ${la} 1 ${oex},${oey} L${iex},${iey} A${ir},${ir} 0 ${la} 0 ${isx},${isy}Z`;
}

function makeArcPath(cx, cy, r, startDeg, endDeg) {
  const toRad = d => d * Math.PI / 180;
  const sx = cx + r * Math.cos(toRad(startDeg));
  const sy = cy + r * Math.sin(toRad(startDeg));
  const ex = cx + r * Math.cos(toRad(endDeg));
  const ey = cy + r * Math.sin(toRad(endDeg));
  // Flip when text would read right-to-left
  if (sx > ex) {
    const la = (endDeg - startDeg) > 180 ? 1 : 0;
    return `M${ex},${ey} A${r},${r} 0 ${la} 1 ${sx},${sy}`;
  }
  const la = (endDeg - startDeg) > 180 ? 1 : 0;
  return `M${sx},${sy} A${r},${r} 0 ${la} 1 ${ex},${ey}`;
}

// Navy blue accent (replaces ProfitForged red)
const ACCENT = '#1e40af';
const ACCENT_LIGHT = '#3b82f6';
const ACCENT_GRAD = 'linear-gradient(145deg, #1e40af, #1e3a8a)';
const SILVER_GRAD = 'linear-gradient(145deg, #e8e8e8 0%, #c0c0c0 20%, #f5f5f5 40%, #b8b8b8 60%, #d8d8d8 80%, #e0e0e0 100%)';

export class DonutSelector {
  constructor(onModuleSwitch) {
    this.onModuleSwitch = onModuleSwitch;
    this.isOpen = false;
    this.highlightedIndex = -1;
    this.activeModule = 'home';
    this.overlay = null;
    this._escHandler = null;
    this._setupCoin();
    this._setupDockSlots();
  }

  _setupCoin() {
    const coin = document.getElementById('dock-coin');
    if (coin) coin.addEventListener('click', () => this.open());
    this._updateCoinDisplay();
  }

  _setupDockSlots() {
    document.querySelectorAll('.dock-slot[data-module]').forEach(slot => {
      slot.addEventListener('click', () => this._switchTo(slot.dataset.module));
    });
    this._updateDockSlots();
  }

  _switchTo(moduleId) {
    this.activeModule = moduleId;
    this._updateCoinDisplay();
    this._updateDockSlots();
    if (this.onModuleSwitch) this.onModuleSwitch(moduleId);
  }

  _updateCoinDisplay() {
    const item = ITEMS.find(i => i.id === this.activeModule);
    const iconEl = document.getElementById('dock-coin-icon');
    const labelEl = document.getElementById('dock-coin-label');
    if (item && iconEl) iconEl.textContent = item.icon;
    if (item && labelEl) labelEl.textContent = item.label;
  }

  _updateDockSlots() {
    document.querySelectorAll('.dock-slot[data-module]').forEach(slot => {
      slot.classList.toggle('active', slot.dataset.module === this.activeModule);
    });
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;
    this.highlightedIndex = -1;
    this._build();
    document.body.style.overflow = 'hidden';
  }

  close(moduleId) {
    if (!this.isOpen) return;
    this.isOpen = false;
    document.body.style.overflow = '';
    if (this.overlay) { this.overlay.remove(); this.overlay = null; }
    if (this._escHandler) { document.removeEventListener('keydown', this._escHandler); this._escHandler = null; }
    if (moduleId) this._switchTo(moduleId);
  }

  // ==========================================
  // BUILD — exact ProfitForged dimensions
  // ==========================================

  _build() {
    const NS = 'http://www.w3.org/2000/svg';
    const CX = 240, CY = 240, SIZE = 480;
    const SLOT_ANGLE = 360 / ITEMS.length;

    // Overlay backdrop
    const overlay = document.createElement('div');
    overlay.id = 'donut-overlay';
    overlay.style.cssText = `
      position:fixed;inset:0;z-index:100;
      background:rgba(0,0,0,0.4);
      backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px);
      touch-action:none;overscroll-behavior:contain;
    `;
    overlay.addEventListener('click', (e) => { if (e.target === overlay) this.close(); });

    // Container
    const ctr = document.createElement('div');
    ctr.style.cssText = `
      position:absolute;left:50%;top:50%;
      width:${SIZE}px;height:${SIZE}px;
      transform:translate(-50%,-50%);
      overflow:visible;
    `;
    // Scale for small screens
    const maxFit = Math.min(window.innerWidth, window.innerHeight) - 24;
    const scale = Math.min(1, maxFit / (SIZE + 16));
    if (scale < 1) ctr.style.transform = `translate(-50%,-50%) scale(${scale})`;

    // ---- Layer 1: SVG wedges ----
    const svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('width', SIZE);
    svg.setAttribute('height', SIZE);
    svg.style.cssText = 'position:absolute;left:0;top:0;pointer-events:none;z-index:0;overflow:visible;';

    // Defs: metallic gradient + arc paths
    const defs = document.createElementNS(NS, 'defs');
    const grad = document.createElementNS(NS, 'linearGradient');
    grad.id = 'metallic-silver';
    grad.setAttribute('x1', '0%'); grad.setAttribute('y1', '0%');
    grad.setAttribute('x2', '0%'); grad.setAttribute('y2', '100%');
    ['#F5F5F5','#E0E0E0','#D0D0D0','#B8B8B8','#A8A8A8','#909090'].forEach((c, i) => {
      const stop = document.createElementNS(NS, 'stop');
      stop.setAttribute('offset', `${i * 20}%`);
      stop.setAttribute('stop-color', c);
      grad.appendChild(stop);
    });
    defs.appendChild(grad);

    // Arc paths for category text
    CATEGORIES.forEach(cat => {
      const sd = cat.startIndex * SLOT_ANGLE - 90 - SLOT_ANGLE / 2;
      const ed = cat.endIndex * SLOT_ANGLE - 90 + SLOT_ANGLE / 2;
      const textArc = document.createElementNS(NS, 'path');
      textArc.id = `text-arc-${cat.id}`;
      textArc.setAttribute('d', makeArcPath(CX, CY, 216, sd, ed));
      textArc.setAttribute('fill', 'none');
      defs.appendChild(textArc);
    });
    svg.appendChild(defs);

    // Wedge shapes per category
    CATEGORIES.forEach(cat => {
      const sd = cat.startIndex * SLOT_ANGLE - 90 - SLOT_ANGLE / 2;
      const ed = cat.endIndex * SLOT_ANGLE - 90 + SLOT_ANGLE / 2;

      // Wedge group (scales from 0.74 to 1)
      const g = document.createElementNS(NS, 'g');
      g.dataset.catId = cat.id;
      g.style.transformOrigin = `${CX}px ${CY}px`;
      g.style.transform = 'scale(0.74)';
      g.style.opacity = '0';
      g.style.transition = 'transform 300ms cubic-bezier(0.4,0,0.2,1), opacity 250ms cubic-bezier(0.4,0,0.2,1)';

      const wedge = document.createElementNS(NS, 'path');
      wedge.setAttribute('d', makeWedgePath(CX, CY, 165, 228, sd, ed));
      wedge.setAttribute('fill', cat.color);
      wedge.setAttribute('fill-opacity', '0.92');
      wedge.setAttribute('stroke', 'url(#metallic-silver)');
      wedge.setAttribute('stroke-width', '4');
      wedge.setAttribute('filter', 'drop-shadow(0 2px 6px rgba(0,0,0,0.2))');
      g.appendChild(wedge);

      // Inner highlight line
      const highlight = document.createElementNS(NS, 'path');
      highlight.setAttribute('d', makeWedgePath(CX, CY, 168, 225, sd, ed));
      highlight.setAttribute('fill', 'none');
      highlight.setAttribute('stroke', 'rgba(255,255,255,0.25)');
      highlight.setAttribute('stroke-width', '1');
      g.appendChild(highlight);
      svg.appendChild(g);

      // Text group (only opacity, no scale)
      const tg = document.createElementNS(NS, 'g');
      tg.dataset.catTextId = cat.id;
      tg.style.opacity = '0';
      tg.style.transition = 'opacity 200ms cubic-bezier(0.4,0,0.2,1) 100ms';

      const text = document.createElementNS(NS, 'text');
      text.setAttribute('fill', 'white');
      text.setAttribute('font-size', '11');
      text.setAttribute('font-weight', '700');
      text.setAttribute('letter-spacing', '0.1em');
      text.setAttribute('text-anchor', 'middle');
      const tp = document.createElementNS(NS, 'textPath');
      tp.setAttributeNS('http://www.w3.org/1999/xlink', 'href', `#text-arc-${cat.id}`);
      tp.setAttribute('startOffset', '50%');
      tp.textContent = cat.label;
      text.appendChild(tp);
      tg.appendChild(text);
      svg.appendChild(tg);
    });

    ctr.appendChild(svg);

    // ---- Layer 2: Outer silver ring (380px) ----
    const outerRing = document.createElement('div');
    outerRing.style.cssText = `
      position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);
      width:380px;height:380px;border-radius:50%;z-index:1;
      background:${SILVER_GRAD};
      box-shadow:inset 0 2px 6px rgba(255,255,255,0.9),inset 0 -2px 6px rgba(0,0,0,0.2),0 8px 32px rgba(0,0,0,0.3);
    `;
    ctr.appendChild(outerRing);

    // ---- Layer 2b: Inner cutout (320px) ----
    const innerCut = document.createElement('div');
    innerCut.style.cssText = `
      position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);
      width:320px;height:320px;border-radius:50%;z-index:2;
      background:rgba(0,0,0,0.3);
      box-shadow:inset 0 2px 8px rgba(0,0,0,0.3);
    `;
    ctr.appendChild(innerCut);

    // ---- Layer 3: Navigation items (52px, at r=168) ----
    const ITEM_SIZE = 48;
    const RING_WIDTH = 4;
    const TOTAL_SIZE = ITEM_SIZE + RING_WIDTH * 2; // 56px total with ring
    ITEMS.forEach((item, i) => {
      const angle = (i * SLOT_ANGLE - 90) * (Math.PI / 180);
      const x = CX + Math.cos(angle) * 168;
      const y = CY + Math.sin(angle) * 168;

      const wrap = document.createElement('div');
      wrap.className = 'donut-item-wrap';
      wrap.dataset.index = i;
      wrap.style.cssText = `
        position:absolute;left:${x - TOTAL_SIZE / 2}px;top:${y - TOTAL_SIZE / 2}px;
        width:${TOTAL_SIZE}px;height:${TOTAL_SIZE}px;
        display:flex;align-items:center;justify-content:center;
        cursor:pointer;touch-action:none;z-index:3;
        transition:transform 150ms;
      `;

      // Silver ring (same as center coin)
      const ring = document.createElement('div');
      ring.className = 'donut-item-ring';
      ring.style.cssText = `
        position:absolute;inset:0;border-radius:50%;
        background:${SILVER_GRAD};
        box-shadow:inset 0 1px 3px rgba(255,255,255,0.9),inset 0 -1px 3px rgba(0,0,0,0.15),0 2px 8px rgba(0,0,0,0.15);
      `;
      wrap.appendChild(ring);

      const isActive = item.id === this.activeModule;
      const INACTIVE_BG = 'linear-gradient(145deg, #f0f2f8 0%, #dfe3ef 50%, #e8eaf2 100%)';
      const btn = document.createElement('div');
      btn.className = 'donut-item-btn';
      btn.style.cssText = `
        position:relative;
        width:${ITEM_SIZE}px;height:${ITEM_SIZE}px;
        border-radius:50%;
        background:${isActive ? ACCENT_GRAD : INACTIVE_BG};
        color:${isActive ? 'white' : '#374151'};
        transition:all 150ms;
        box-shadow:${isActive ? '0 2px 8px rgba(30,64,175,0.4)' : 'inset 0 1px 2px rgba(255,255,255,0.8), inset 0 -1px 2px rgba(0,0,0,0.06)'};
        display:flex;align-items:center;justify-content:center;
        z-index:1;
        overflow:hidden;
      `;

      const icon = document.createElement('span');
      icon.textContent = item.icon;
      icon.style.cssText = 'font-size:22px;line-height:1;display:flex;align-items:center;justify-content:center;width:100%;height:100%;';
      btn.appendChild(icon);

      wrap.appendChild(btn);

      wrap.addEventListener('mouseenter', () => { this.highlightedIndex = i; this._updateHighlight(ctr); });
      wrap.addEventListener('mouseleave', () => { this.highlightedIndex = -1; this._updateHighlight(ctr); });
      wrap.addEventListener('touchstart', (e) => {
        e.preventDefault(); e.stopPropagation();
        this.highlightedIndex = i; this._updateHighlight(ctr);
      }, { passive: false });
      wrap.addEventListener('click', (e) => { e.stopPropagation(); this.close(item.id); });
      wrap.addEventListener('touchend', (e) => {
        e.preventDefault(); e.stopPropagation();
        this.close(item.id);
      });

      ctr.appendChild(wrap);
    });

    // ---- Layer 4: Center coin (110px, navy blue) ----
    const center = document.createElement('div');
    center.style.cssText = `
      position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);z-index:10;
    `;

    const coinRing = document.createElement('div');
    coinRing.style.cssText = `
      position:absolute;inset:-8px;border-radius:50%;
      background:${SILVER_GRAD};
      box-shadow:inset 0 2px 4px rgba(255,255,255,0.9),inset 0 -2px 4px rgba(0,0,0,0.15),0 4px 16px rgba(0,0,0,0.2);
    `;
    center.appendChild(coinRing);

    const coin = document.createElement('div');
    coin.style.cssText = `
      position:relative;display:flex;flex-direction:column;align-items:center;justify-content:center;
      width:110px;height:110px;border-radius:50%;
      background:${ACCENT_GRAD};
      box-shadow:0 4px 16px rgba(30,58,138,0.3),inset 0 1px 2px rgba(255,255,255,0.2);
    `;

    const cIcon = document.createElement('span');
    cIcon.id = 'donut-center-icon';
    cIcon.style.cssText = 'font-size:32px;color:white;line-height:1;';
    const cLabel = document.createElement('span');
    cLabel.id = 'donut-center-label';
    cLabel.style.cssText = 'font-size:10px;font-weight:600;color:rgba(255,255,255,0.9);text-transform:uppercase;letter-spacing:0.05em;margin-top:2px;';
    const cSub = document.createElement('span');
    cSub.id = 'donut-center-sub';
    cSub.style.cssText = 'font-size:7px;font-weight:600;text-transform:uppercase;letter-spacing:0.1em;margin-top:1px;color:rgba(255,255,255,0.7);';

    const activeItem = ITEMS.find(i => i.id === this.activeModule);
    cIcon.textContent = activeItem ? activeItem.icon : '⚒';
    cLabel.textContent = activeItem ? activeItem.label : 'MENU';
    cSub.textContent = '';

    coin.appendChild(cIcon);
    coin.appendChild(cLabel);
    coin.appendChild(cSub);
    center.appendChild(coin);
    ctr.appendChild(center);

    overlay.appendChild(ctr);
    document.body.appendChild(overlay);
    this.overlay = overlay;

    this._escHandler = (e) => { if (e.key === 'Escape') this.close(); };
    document.addEventListener('keydown', this._escHandler);
  }

  // ==========================================
  // HIGHLIGHT — matches ProfitForged behavior
  // ==========================================

  _updateHighlight(ctr) {
    const hi = this.highlightedIndex;
    const hCat = hi >= 0 ? getCatForIndex(hi) : null;
    const activeCat = hi >= 0 ? hCat : null;

    // Update wedges
    ctr.querySelectorAll('g[data-cat-id]').forEach(g => {
      const isActive = activeCat && g.dataset.catId === activeCat.id;
      g.style.transform = isActive ? 'scale(1)' : 'scale(0.74)';
      g.style.opacity = isActive ? '1' : '0';
    });
    ctr.querySelectorAll('g[data-cat-text-id]').forEach(g => {
      const isActive = activeCat && g.dataset.catTextId === activeCat.id;
      g.style.opacity = isActive ? '1' : '0';
    });

    // Update items
    ctr.querySelectorAll('.donut-item-wrap').forEach(wrap => {
      const i = parseInt(wrap.dataset.index);
      const isHi = i === hi;
      const itemCat = getCatForIndex(i);
      const isRelated = hi >= 0 && !isHi && activeCat && itemCat && itemCat.id === activeCat.id;
      const btn = wrap.querySelector('.donut-item-btn');

      if (isHi) {
        wrap.style.zIndex = '5';
        wrap.style.transform = 'scale(1.15)';
        btn.style.background = ACCENT_LIGHT;
        btn.style.color = 'white';
        btn.style.boxShadow = '0 2px 12px rgba(30,64,175,0.4)';
      } else if (isRelated) {
        wrap.style.zIndex = '3';
        wrap.style.transform = 'scale(1.08)';
        btn.style.background = ACCENT_LIGHT;
        btn.style.color = 'white';
        btn.style.boxShadow = '0 2px 8px rgba(30,64,175,0.3)';
      } else if (ITEMS[i].id === this.activeModule) {
        wrap.style.zIndex = '4';
        wrap.style.transform = 'scale(1.08)';
        btn.style.background = ACCENT_GRAD;
        btn.style.color = 'white';
        btn.style.boxShadow = '0 2px 12px rgba(30,64,175,0.4)';
      } else {
        wrap.style.zIndex = '3';
        wrap.style.transform = 'scale(1)';
        btn.style.background = 'linear-gradient(145deg, #f0f2f8 0%, #dfe3ef 50%, #e8eaf2 100%)';
        btn.style.color = '#374151';
        btn.style.boxShadow = 'inset 0 1px 2px rgba(255,255,255,0.8), inset 0 -1px 2px rgba(0,0,0,0.06)';
      }
    });

    // Update center coin
    const cIcon = document.getElementById('donut-center-icon');
    const cLabel = document.getElementById('donut-center-label');
    const cSub = document.getElementById('donut-center-sub');
    if (cIcon && cLabel && cSub) {
      if (hi >= 0) {
        cIcon.textContent = ITEMS[hi].icon;
        cLabel.textContent = ITEMS[hi].label;
        cSub.textContent = hCat ? hCat.label : '';
      } else {
        const a = ITEMS.find(i => i.id === this.activeModule);
        cIcon.textContent = a ? a.icon : '⚒';
        cLabel.textContent = a ? a.label : 'MENU';
        cSub.textContent = '';
      }
    }
  }
}
